import type { PrimarySurface } from '../contracts/types'

export type CommandDeckDensity = 'comfortable' | 'compact'
export type CommandDeckMotionPref = 'system' | 'reduce'
export type CommandDeckDefaultSurfacePref = 'command_deck' | 'guided_studio' | 'remember_last'
export type CommandDeckGsEntryPref = 'intake' | 'resume_last'

export type CommandDeckCockpitPrefs = {
  density: CommandDeckDensity
  motion: CommandDeckMotionPref
  defaultSurface: CommandDeckDefaultSurfacePref
  gsEntry: CommandDeckGsEntryPref
}

const STORAGE_KEY = 'synktribe.commandDeck.cockpitPrefs'

export const DEFAULT_COMMAND_DECK_COCKPIT_PREFS: CommandDeckCockpitPrefs = {
  density: 'comfortable',
  motion: 'system',
  defaultSurface: 'command_deck',
  gsEntry: 'intake',
}

function isDensity(value: unknown): value is CommandDeckDensity {
  return value === 'comfortable' || value === 'compact'
}

function isMotion(value: unknown): value is CommandDeckMotionPref {
  return value === 'system' || value === 'reduce'
}

function isDefaultSurface(value: unknown): value is CommandDeckDefaultSurfacePref {
  return value === 'command_deck' || value === 'guided_studio' || value === 'remember_last'
}

function isGsEntry(value: unknown): value is CommandDeckGsEntryPref {
  return value === 'intake' || value === 'resume_last'
}

export function normalizeCommandDeckCockpitPrefs(
  partial: Partial<CommandDeckCockpitPrefs> | null | undefined
): CommandDeckCockpitPrefs {
  if (!partial) return { ...DEFAULT_COMMAND_DECK_COCKPIT_PREFS }
  return {
    density: isDensity(partial.density) ? partial.density : DEFAULT_COMMAND_DECK_COCKPIT_PREFS.density,
    motion: isMotion(partial.motion) ? partial.motion : DEFAULT_COMMAND_DECK_COCKPIT_PREFS.motion,
    defaultSurface: isDefaultSurface(partial.defaultSurface)
      ? partial.defaultSurface
      : DEFAULT_COMMAND_DECK_COCKPIT_PREFS.defaultSurface,
    gsEntry: isGsEntry(partial.gsEntry) ? partial.gsEntry : DEFAULT_COMMAND_DECK_COCKPIT_PREFS.gsEntry,
  }
}

export function loadCommandDeckCockpitPrefs(): CommandDeckCockpitPrefs {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_COMMAND_DECK_COCKPIT_PREFS }
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_COMMAND_DECK_COCKPIT_PREFS }
    return normalizeCommandDeckCockpitPrefs(JSON.parse(raw) as Partial<CommandDeckCockpitPrefs>)
  } catch {
    return { ...DEFAULT_COMMAND_DECK_COCKPIT_PREFS }
  }
}

export function saveCommandDeckCockpitPrefs(prefs: CommandDeckCockpitPrefs): CommandDeckCockpitPrefs {
  const next = normalizeCommandDeckCockpitPrefs(prefs)
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // no-op
    }
  }
  return next
}

export function describeDefaultSurfacePref(pref: CommandDeckDefaultSurfacePref): string {
  switch (pref) {
    case 'command_deck':
      return 'Command Deck'
    case 'guided_studio':
      return 'Guided Studio'
    case 'remember_last':
      return 'Remember last surface (preference only)'
    default:
      return pref
  }
}

export function describeGsEntryPref(pref: CommandDeckGsEntryPref): string {
  return pref === 'intake' ? 'Guided Studio intake' : 'Resume last session (when available)'
}

export function primarySurfaceLabel(surface: PrimarySurface): string {
  if (surface === 'guided_studio') return 'Guided Studio'
  if (surface === 'omnistudio') return 'OmniStudio'
  if (surface === 'none') return 'None'
  return surface.replace(/_/g, ' ')
}
